import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";

const BG_KEY = "cosx-bg-style";

export const BG_OPTIONS = [
  { value: "animated",     label: "Circles",      overlayOpacity: 0.75 },
  { value: "wave",         label: "Wave",          overlayOpacity: 0.55 },
  { value: "grid",         label: "Grid",          overlayOpacity: 0.58 },
  { value: "interference", label: "Interference",  overlayOpacity: 0.55 },
  { value: "static",       label: "Static",        overlayOpacity: 0.70 },
] as const;

export type BgStyle = (typeof BG_OPTIONS)[number]["value"];

export function getOverlayOpacity(style: BgStyle): number {
  return BG_OPTIONS.find(o => o.value === style)?.overlayOpacity ?? 0.75;
}

const ALL_VALUES = BG_OPTIONS.map((o) => o.value);
const AUTO_VALUES = ALL_VALUES;

export function getInitialBgStyle(): BgStyle {
  if (typeof window === "undefined") return "animated";
  const stored = localStorage.getItem(BG_KEY);
  if (stored && ALL_VALUES.includes(stored as BgStyle)) return stored as BgStyle;
  // Default to "animated" for SSR/client hydration consistency
  // Auto-rotate in BackgroundContext will randomise after mount
  return "animated";
}

export function saveBgStyle(style: BgStyle) {
  localStorage.setItem(BG_KEY, style);
}

export function pickRandomNext(current: BgStyle): BgStyle {
  const others = AUTO_VALUES.filter((v) => v !== current);
  return others[Math.floor(Math.random() * others.length)]!;
}

export function BackgroundSelector({
  value,
  onChange,
  className,
}: {
  value: BgStyle;
  onChange: (style: BgStyle) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const current = BG_OPTIONS.find((o) => o.value === value);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1.5 px-3 py-1.5 rounded-full",
          "border border-[var(--color-border)] bg-[var(--color-surface)]",
          "text-[10px] font-[family-name:var(--font-mono)] tracking-wider uppercase",
          "text-[var(--color-fg-secondary)] hover:border-[var(--color-accent)] transition-colors"
        )}
      >
        {current?.label ?? "BG"}
        <svg
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={cn("transition-transform", open && "rotate-180")}
        >
          <path d="M1.5 3L4 5.5L6.5 3" />
        </svg>
      </button>

      {open && (
        <div
          className={cn(
            "absolute right-0 bottom-full mb-2 py-1 min-w-[140px] rounded-lg",
            "border border-[var(--color-border)] bg-[var(--color-surface)]",
            "shadow-lg z-[60]"
          )}
        >
          {BG_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={cn(
                "w-full text-left px-4 py-2 text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase transition-colors",
                value === opt.value
                  ? "text-[var(--color-accent)] bg-[var(--color-bg-secondary)]"
                  : "text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:bg-[var(--color-bg-secondary)]"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
